import { Store } from "@/api/storeService";
import { useNavigate } from "@/hooks/useNavigate";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { storeEventProps } from "@/lib/posthog";
import { Facebook, Instagram, MessageCircle, Globe, ArrowLeft, BadgeCheck } from "lucide-react";

function getLogoGradient(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(hash) % 360;
  return `linear-gradient(135deg, hsl(${hue},55%,42%), hsl(${(hue + 30) % 360},55%,38%))`;
}

const STAR_POINTS =
  "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2";
const STAR_SIZE = 13;

function StarRow({ rating }: { rating: number }) {
  return (
    // direction: ltr so the 5 stars and their partial fills render left-to-right
    // regardless of the card's RTL context.
    <span className="flex items-center gap-px" style={{ direction: "ltr" }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.min(Math.max(rating - i, 0), 1);
        return (
          <span
            key={i}
            className="relative inline-block flex-shrink-0"
            style={{ width: STAR_SIZE, height: STAR_SIZE }}
          >
            {/* Base gray star */}
            <svg
              width={STAR_SIZE}
              height={STAR_SIZE}
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              className="text-[#a8a09a]"
              style={{ opacity: 0.22, display: "block" }}
            >
              <polygon points={STAR_POINTS} />
            </svg>
            {/* Gold overlay clipped to the fractional fill width */}
            <span
              className="absolute overflow-hidden"
              style={{ left: 0, top: 0, height: "100%", width: `${fill * 100}%` }}
            >
              <svg
                width={STAR_SIZE}
                height={STAR_SIZE}
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
                style={{ color: "oklch(0.78 0.13 80)", flexShrink: 0, display: "block", width: STAR_SIZE }}
              >
                <polygon points={STAR_POINTS} />
              </svg>
            </span>
          </span>
        );
      })}
    </span>
  );
}

function Dot() {
  return (
    <span
      className="inline-block rounded-full flex-shrink-0"
      style={{ width: 3, height: 3, background: "#a8a09a" }}
    />
  );
}

function formatFollowers(n: number): string | null {
  if (n < 1000) return null;
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}

type Props = { store: Store };

export function StoreListCard({ store }: Props) {
  const navigate = useNavigate();
  const { trackStore } = usePostHogTracking();
  const storePath = `/store/${store.slug || store._id}`;
  const trackContact = (platform: string) =>
    trackStore.contactClicked({
      platform,
      surface: "list_card",
      ...storeEventProps(store),
    });
  const categoryLabel =
    store.prominentCategory?.category?.title?.ar || store.category?.title?.ar;
  const tags = (store.tags || []).slice(0, 3);
  const hasRating = store.totalReviews > 0;
  const platformMetrics = store.metadata?.platformMetrics as {
    facebook?: { followers?: number; likes?: number };
    instagram?: { followers?: number };
  } | undefined;
  const fbFollowers = platformMetrics?.facebook?.followers ?? platformMetrics?.facebook?.likes ?? 0;
  const igFollowers = platformMetrics?.instagram?.followers ?? 0;
  const totalFollowers = fbFollowers + igFollowers;
  const formattedFollowers = formatFollowers(totalFollowers);
  const hasSubLine = true;

  return (
    <div
      onClick={() => navigate(storePath)}
      className="bg-white rounded-2xl border cursor-pointer transition-all duration-[180ms] ease-[ease] hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-16px_rgba(20,17,15,0.18)] hover:border-[#e0d9d1] h-full"
      style={{ padding: "18px 20px", borderColor: "#ece7e1", display: "flex", flexDirection: "column" }}
    >
      {/* Row 1: logo + identity */}
      <div className={`flex gap-3 ${hasSubLine ? "items-start" : "items-center"}`}>
        {/* Logo tile */}
        <div
          className="flex-shrink-0 overflow-hidden"
          style={{ width: 56, height: 56, borderRadius: 12, background: "#f4efe8", border: "1px solid #ece7e1" }}
        >
          {store.logoUrl ? (
            <img src={store.logoUrl} alt={store.name} className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-white font-bold"
              style={{ fontSize: 20, background: getLogoGradient(store._id) }}
            >
              {store.name.charAt(0)}
            </div>
          )}
        </div>

        {/* Identity block */}
        <div className="flex-1 min-w-0">
          {/* Name + verified */}
          <div className="flex items-center gap-1">
            <h3
              className="truncate font-bold"
              style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25, color: "#14110f" }}
            >
              {store.name}
            </h3>
            {store.verified && (
              <BadgeCheck
                className="flex-shrink-0"
                style={{ width: 14, height: 14, color: "oklch(0.62 0.14 155)" }}
                aria-label="متجر موثّق"
              />
            )}
          </div>

          {/* Sub-line: rating · city */}
          <div
            className="flex items-center flex-wrap"
            style={{ fontSize: 12.5, color: "#736b63", marginTop: 3, gap: 8, lineHeight: 1.4 }}
          >
            {hasRating && (
              <span className="flex items-center gap-1">
                <StarRow rating={store.rating} />
                <span style={{ fontWeight: 700, color: "#14110f" }}>{store.rating.toFixed(1)}</span>
                <span>({store.totalReviews})</span>
              </span>
            )}
            {/* Followers count */}
            {formattedFollowers && (
              <>
                {hasRating && <Dot />}
                <span>{formattedFollowers}</span>
              </>
            )}
            {/* New-store chip — only when no reviews */}
            {!hasRating && (
              <span
                style={{
                  background: "#f4efe8",
                  color: "#736b63",
                  border: "1px solid #ece7e1",
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 500,
                }}
              >
                متجر جديد
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {store.description && (
        <p
          className="line-clamp-2"
          style={{ fontSize: 13.5, lineHeight: 1.55, color: "#736b63", marginTop: 14 }}
        >
          {store.description}
        </p>
      )}

      {/* Tags row */}
      {(categoryLabel || tags.length > 0) && (
        <div className="flex flex-wrap" style={{ gap: 6, marginTop: 14 }}>
          {categoryLabel && (
            <span
              style={{
                background: "oklch(0.96 0.04 60)",
                color: "oklch(0.45 0.15 45)",
                padding: "3px 9px",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 500,
              }}
            >
              {categoryLabel}
            </span>
          )}
          {tags.map((tag, i) => (
            <span
              key={i}
              style={{
                background: "#f4efe8",
                color: "#3a3530",
                border: "1px solid #ece7e1",
                padding: "3px 9px",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 500,
              }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions row */}
      <div className="flex items-center justify-between" style={{ marginTop: "auto", paddingTop: 16 }}>
        {/* Social icons */}
        <div className="flex" style={{ gap: 4 }}>
          {store.platforms?.facebook && (
            <a
              href={store.platforms?.facebook}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackContact("facebook");
              }}
              aria-label="فيسبوك"
              className="flex items-center justify-center transition-colors hover:bg-[#ece7e1]"
              style={{ width: 28, height: 28, borderRadius: 7, background: "#f4efe8", color: "#3a3530" }}
            >
              <Facebook style={{ width: 14, height: 14 }} />
            </a>
          )}
          {store.platforms?.instagram && (
            <a
              href={store.platforms?.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackContact("instagram");
              }}
              aria-label="إنستغرام"
              className="flex items-center justify-center transition-colors hover:bg-[#ece7e1]"
              style={{ width: 28, height: 28, borderRadius: 7, background: "#f4efe8", color: "#3a3530" }}
            >
              <Instagram style={{ width: 14, height: 14 }} />
            </a>
          )}
          {store.platforms?.whatsapp && (
            <a
              href={`https://wa.me/${store.platforms?.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackContact("whatsapp");
              }}
              aria-label="واتساب"
              className="flex items-center justify-center transition-colors hover:bg-[#ece7e1]"
              style={{ width: 28, height: 28, borderRadius: 7, background: "#f4efe8", color: "#3a3530" }}
            >
              <MessageCircle style={{ width: 14, height: 14 }} />
            </a>
          )}
          {store.platforms?.website && (
            <a
              href={store.platforms?.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackContact("website");
              }}
              aria-label="الموقع الإلكتروني"
              className="flex items-center justify-center transition-colors hover:bg-[#ece7e1]"
              style={{ width: 28, height: 28, borderRadius: 7, background: "#f4efe8", color: "#3a3530" }}
            >
              <Globe style={{ width: 14, height: 14 }} />
            </a>
          )}
        </div>

        {/* CTA — hidden on mobile */}
        <a
          href={storePath}
          onClick={(e) => e.stopPropagation()}
          className="hidden md:flex items-center gap-1 transition-colors hover:bg-[#f4efe8]"
          style={{
            color: "#14110f",
            border: "1px solid #e0d9d1",
            padding: "8px 14px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          عرض المتجر
          <ArrowLeft style={{ width: 12, height: 12 }} />
        </a>
      </div>
    </div>
  );
}

const shimmerStyle = {
  backgroundImage: "linear-gradient(90deg, #f4efe8 25%, #ece7e1 50%, #f4efe8 75%)",
  backgroundSize: "200% 100%",
};

export function StoreListCardSkeleton() {
  return (
    <div
      className="bg-white rounded-2xl border"
      style={{ padding: "18px 20px", borderColor: "#ece7e1" }}
    >
      <div className="flex gap-3 items-start">
        <div
          className="flex-shrink-0 animate-shimmer"
          style={{ width: 56, height: 56, borderRadius: 12, ...shimmerStyle }}
        />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="h-[17px] animate-shimmer rounded" style={{ width: "60%", ...shimmerStyle }} />
          <div className="h-3 animate-shimmer rounded" style={{ width: "80%", ...shimmerStyle }} />
        </div>
      </div>
      <div className="flex flex-col gap-2" style={{ marginTop: 14 }}>
        <div className="h-3 animate-shimmer rounded" style={{ ...shimmerStyle }} />
        <div className="h-3 animate-shimmer rounded" style={{ width: "85%", ...shimmerStyle }} />
      </div>
      <div className="flex gap-1.5" style={{ marginTop: 14 }}>
        {[64, 80, 72].map((w, i) => (
          <div
            key={i}
            className="h-5 animate-shimmer"
            style={{ width: w, borderRadius: 999, ...shimmerStyle }}
          />
        ))}
      </div>
    </div>
  );
}
